import { clearAuthData, getAuthToken, getUserData } from "./storage";
import { shouldLogoutOnFetchUnauthorized } from "./authErrors";
import { getRefreshedAccessToken } from "./tokenRefresh";

const FORCE_LOGOUT_EVENT_KEY = "forceLogoutEvent";
const BROADCAST_CHANNEL_NAME = "school-admin-force-logout";
const LOGOUT_EVENT_TYPE = "forceLogout";
const DEFAULT_POLL_INTERVAL_MS = 30000; // 30 seconds - reasonable interval to check admin assignment without excessive API calls
const CACHE_DURATION_MS = 5000; // 5 seconds - cache school data briefly to avoid rapid requests, but allow quick detection of changes

export interface ForceLogoutConfig {
  userId: number;
  schoolId?: number | null;
  logout: () => void;
  pollIntervalMs?: number;
}

interface LogoutPayload {
  type: string;
  reason: string;
  ts: number;
  targetAdminIds?: string[];
}

const isClient = typeof window !== "undefined";

let currentConfig: ForceLogoutConfig | null = null;
let pollingTimer: number | null = null;
let storageListenerAdded = false;
let broadcastChannel: BroadcastChannel | null = null;
let logoutTriggered = false;
let visibilityListenerAdded = false;
let schoolDataCache: { schoolId: number; adminIds: string[]; timestamp: number } | null = null;

const parsePayload = (raw: unknown): LogoutPayload | null => {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as LogoutPayload;
    } catch {
      return null;
    }
  }
  if (typeof raw === "object" && (raw as LogoutPayload).type === LOGOUT_EVENT_TYPE) {
    return raw as LogoutPayload;
  }
  return null;
};

const shouldProcessPayload = (payload: LogoutPayload | null) => {
  if (!payload) return false;
  const targets = payload.targetAdminIds;
  
  // If no targets specified, logout everyone (broadcast to all)
  if (!targets || targets.length === 0) return true;
  
  // Check for school-based logout (format: "school:${schoolId}")
  const schoolTargets = targets.filter(t => typeof t === 'string' && t.startsWith("school:"));
  if (schoolTargets.length > 0) {
    // Extract school IDs from targets
    const schoolIds = schoolTargets.map(t => String(t).replace("school:", ""));
    
    // Check currentConfig first (for school admins with force logout guard enabled)
    if (currentConfig && currentConfig.schoolId && schoolIds.includes(String(currentConfig.schoolId))) {
      return true;
    }
    
    // Also check user data from storage (for teachers/students without force logout guard)
    try {
      const userString = getUserData();
      if (userString) {
        const userData = JSON.parse(userString);
        const userSchoolId = userData?.schoolId;
        if (userSchoolId && schoolIds.includes(String(userSchoolId))) {
          return true;
        }
      }
    } catch (error) {
    }
    
    return false;
  }
  
  // Check for user-based logout (normal admin revocation) - requires currentConfig
  if (!currentConfig) return false;
  return targets.includes(String(currentConfig.userId));
};

const broadcastLogoutEvent = (reason: string, targetAdminIds?: string[]) => {
  if (!isClient) return;
  const payload: LogoutPayload = {
    type: LOGOUT_EVENT_TYPE,
    reason,
    ts: Date.now(),
    targetAdminIds: targetAdminIds && targetAdminIds.length ? targetAdminIds : undefined,
  };
  try {
    localStorage.setItem(FORCE_LOGOUT_EVENT_KEY, JSON.stringify(payload));
  } catch (error) {
  }
  getChannel()?.postMessage(payload);
};

// Ensure storage listener is always active (even for users without force logout guard)
// This allows teachers/students to receive school deletion logout events
const ensureStorageListener = () => {
  if (!isClient || storageListenerAdded) return;
  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key === FORCE_LOGOUT_EVENT_KEY && event.newValue) {
      handleIncomingPayload(event.newValue);
    }
  });
  // Also check localStorage immediately on mount in case event was missed
  try {
    const storedPayload = localStorage.getItem(FORCE_LOGOUT_EVENT_KEY);
    if (storedPayload) {
      const payload = parsePayload(storedPayload);
      // Only process if payload is recent (within last 5 seconds) to avoid stale logouts
      if (payload && payload.ts && (Date.now() - payload.ts) < 5000) {
        handleIncomingPayload(storedPayload);
      }
    }
  } catch (error) {
    // Ignore errors reading localStorage
  }
  storageListenerAdded = true;
};

// Ensure BroadcastChannel listener is always active (even for users without force logout guard)
// This allows teachers/students to receive school deletion logout events
const ensureBroadcastListener = () => {
  if (!isClient) return;
  getChannel(); // This will create the channel and set up the listener
};

const ensureVisibilityListener = () => {
  if (!isClient || visibilityListenerAdded || !document.addEventListener) return;
  const handleVisibilityChange = () => {
    // When tab becomes visible, immediately check for admin changes
    if (!document.hidden && currentConfig && !logoutTriggered) {
      pollAssignment();
    }
  };
  document.addEventListener("visibilitychange", handleVisibilityChange);
  visibilityListenerAdded = true;
};

const clearPolling = () => {
  if (!isClient || pollingTimer === null) return;
  window.clearInterval(pollingTimer);
  pollingTimer = null;
};

const extractAdminIds = (payload: Record<string, any>): string[] => {
  if (!payload) {
    return [];
  }
  
  if (Array.isArray(payload.schoolAdminIds)) {
    const ids = payload.schoolAdminIds
      .filter((id) => id !== undefined && id !== null)
      .map((id) => String(id));
    return ids;
  }
  if (Array.isArray(payload.schoolAdmins)) {
    const ids = payload.schoolAdmins
      .map((admin: any) => admin?.id)
      .filter((id: unknown): id is number | string => id !== undefined && id !== null)
      .map((id) => String(id));
    return ids;
  }
  if (payload.adminId) {
    const ids = [String(payload.adminId)];
    return ids;
  }
  
  return [];
};

const fetchCurrentAdminIds = async (schoolId: number | null | undefined, forceRefresh: boolean = false) => {
  if (!schoolId) return null;
  
  // Check cache first to avoid excessive API calls (unless force refresh is requested)
  if (!forceRefresh && schoolDataCache && schoolDataCache.schoolId === schoolId) {
    const cacheAge = Date.now() - schoolDataCache.timestamp;
    if (cacheAge < CACHE_DURATION_MS) {
      // Return cached data if it's still fresh
      return schoolDataCache.adminIds;
    }
  }
  
  const apiBase = process.env.REACT_APP_API_URL;
  const token = getAuthToken();
  if (!apiBase || !token) return null;

  try {
    let response = await fetch(`${apiBase}/schools/${schoolId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      credentials: "include",
    });

    // Access token expired mid-poll — try a silent refresh and retry once
    // before treating this as an auth failure.
    if (response.status === 401) {
      const newToken = await getRefreshedAccessToken();
      if (newToken) {
        response = await fetch(`${apiBase}/schools/${schoolId}`, {
          headers: {
            Authorization: `Bearer ${newToken}`,
            Accept: "application/json",
          },
          credentials: "include",
        });
      }
    }

    if (response.status === 401 && shouldLogoutOnFetchUnauthorized(`${apiBase}/schools/${schoolId}`)) {
      triggerForceLogout(`poll-${response.status}`);
      return null;
    }
    
    // Check if school was deleted (404) - trigger logout immediately
    if (response.status === 404) {
      triggerForceLogout("school-deleted");
      return null;
    }
    
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    const adminIds = extractAdminIds(data);
    
    // Cache the result (even if empty, to prevent rapid refetches)
    schoolDataCache = {
      schoolId,
      adminIds,
      timestamp: Date.now(),
    };
    
    return adminIds;
  } catch (error) {
    return null;
  }
};

const pollAssignment = async () => {
  if (!currentConfig || !isClient || logoutTriggered) return;
  try {
    const adminIds = await fetchCurrentAdminIds(currentConfig.schoolId ?? null, false);
    
    // If null is returned, it might mean:
    // 1. School was deleted (404) - logout already triggered in fetchCurrentAdminIds
    // 2. API failed or no token - don't trigger logout
    if (adminIds === null) {
      // Check if logout was already triggered (school deleted)
      if (logoutTriggered) {
        return; // Logout already triggered
      }
      // Otherwise, it's an API error - skip this check
      return;
    }
    
    // If empty array, school exists but has no admins - this is unusual but not a logout reason
    if (adminIds.length === 0) {
      return;
    }
    
    const userIdString = String(currentConfig.userId);
    const isUserInAdminList = adminIds.includes(userIdString);
    
    if (!isUserInAdminList) {
      triggerForceLogout("admin-reassigned");
    }
  } catch (error) {
    // Log errors for debugging
  }
};

const startPolling = () => {
  clearPolling();
  if (!currentConfig || !currentConfig.schoolId || !isClient) return;
  // Immediate poll on start
  pollAssignment();
  pollingTimer = window.setInterval(
    pollAssignment,
    currentConfig.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS
  );
};

// Export function to trigger immediate polling check
export const triggerImmediatePoll = () => {
  if (currentConfig && isClient) {
    pollAssignment();
  }
};

function handleIncomingPayload(raw: unknown) {
  const payload = parsePayload(raw);
  if (shouldProcessPayload(payload)) {
    
    // For school deletion, we need to handle logout even if currentConfig is null
    // (for teachers/students who don't have force logout guard enabled)
    if (payload?.reason === "school-deleted" && !currentConfig) {
      // Trigger logout directly using storage-based approach
      clearAuthData();
      if (isClient) {
        // Get homepage base path
        let homepage = process.env.PUBLIC_URL || "/ui/school-erp";
        if (!process.env.PUBLIC_URL && window.location.pathname) {
          const pathParts = window.location.pathname.split('/');
          if (pathParts.length > 1 && pathParts[1] === 'ui' && pathParts[2] === 'school-erp') {
            homepage = `/${pathParts[1]}/${pathParts[2]}`;
          }
        }
        const loginUrl = `${homepage}/#/login`;
        window.location.replace(loginUrl);
      }
      return;
    }
    
    // For other cases, use normal triggerForceLogout (requires currentConfig)
    if (currentConfig) {
      triggerForceLogout(payload?.reason || "force-logout");
    }
  } else {
  }
}

function getChannel() {
  if (!isClient || !("BroadcastChannel" in window)) return null;
  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    broadcastChannel.onmessage = (event) => {
      handleIncomingPayload(event?.data);
    };
  }
  return broadcastChannel;
}

function triggerForceLogout(reason: string) {
  if (!currentConfig || logoutTriggered) return;
  logoutTriggered = true;
  try {
    currentConfig.logout();
  } catch (error) {
  }
  clearAuthData();
  broadcastLogoutEvent(reason, [String(currentConfig.userId)]);
  if (isClient) {
    // Get homepage base path - react-scripts sets PUBLIC_URL from package.json homepage field
    // Homepage is "/ui/school-erp" from package.json, so PUBLIC_URL will be "/ui/school-erp"
    // Fallback to extracting from current pathname or use default
    let homepage = process.env.PUBLIC_URL || "/ui/school-erp";
    
    // If PUBLIC_URL is not set (development), try to extract from current location
    if (!process.env.PUBLIC_URL && window.location.pathname) {
      const pathParts = window.location.pathname.split('/');
      // Extract base path (e.g., "/ui/school-erp" from "/ui/school-erp/#/dashboard")
      if (pathParts.length > 1 && pathParts[1] === 'ui' && pathParts[2] === 'school-erp') {
        homepage = `/${pathParts[1]}/${pathParts[2]}`;
      }
    }
    
    // Construct login URL with hash router format
    const loginUrl = `${homepage}/#/login`;
    window.location.replace(loginUrl);
  }
}

export const enableForceLogoutGuard = (config: ForceLogoutConfig) => {
  if (!isClient) return;
  currentConfig = {
    ...config,
    schoolId: config.schoolId ?? null,
  };
  logoutTriggered = false;
  ensureStorageListener();
  ensureBroadcastListener();
  ensureVisibilityListener();
  startPolling();
};

// Initialize listeners for all users (even without force logout guard)
// This ensures teachers/students can receive school deletion logout events
if (isClient && typeof window !== "undefined") {
  ensureStorageListener();
  ensureBroadcastListener();
}

export const disableForceLogoutGuard = () => {
  clearPolling();
  currentConfig = null;
  logoutTriggered = false;
  schoolDataCache = null; // Clear cache when guard is disabled
};

// Export function to clear cache (useful when admin changes are detected)
export const clearAdminCache = () => {
  schoolDataCache = null;
};

// Export function to trigger immediate poll with cache refresh
export const triggerImmediatePollWithRefresh = () => {
  if (currentConfig && isClient) {
    clearAdminCache();
    pollAssignment();
  }
};

export const broadcastAdminRevocation = (
  adminIds: Array<number | string>,
  reason = "admin-reassigned"
) => {
  if (!isClient || !adminIds?.length) return;
  const targets = adminIds
    .filter((id) => id !== undefined && id !== null)
    .map((id) => String(id));
  if (!targets.length) return;
  
  // Check if current user is in the list of removed admins - logout immediately
  if (currentConfig && targets.includes(String(currentConfig.userId))) {
    triggerForceLogout(reason);
    return;
  }
  
  // Broadcast to same-browser tabs/windows (immediate)
  // This will trigger logout for affected users in other tabs/windows of the same browser
  broadcastLogoutEvent(reason, targets);
};

// Broadcast logout to all users of a deleted school
export const broadcastSchoolDeletion = (schoolId: number | string) => {
  if (!isClient || !schoolId) return;
  
  const schoolIdString = String(schoolId);
  
  // Check if current user is logged into this school - logout immediately
  if (currentConfig && currentConfig.schoolId && String(currentConfig.schoolId) === schoolIdString) {
    triggerForceLogout("school-deleted");
    return;
  }
  
  // Also check user data from storage (for teachers/students without force logout guard)
  try {
    const userString = getUserData();
    if (userString) {
      const userData = JSON.parse(userString);
      const userSchoolId = userData?.schoolId;
      if (userSchoolId && String(userSchoolId) === schoolIdString) {
        clearAuthData();
        if (isClient) {
          let homepage = process.env.PUBLIC_URL || "/ui/school-erp";
          if (!process.env.PUBLIC_URL && window.location.pathname) {
            const pathParts = window.location.pathname.split('/');
            if (pathParts.length > 1 && pathParts[1] === 'ui' && pathParts[2] === 'school-erp') {
              homepage = `/${pathParts[1]}/${pathParts[2]}`;
            }
          }
          const loginUrl = `${homepage}/#/login`;
          window.location.replace(loginUrl);
        }
        return;
      }
    }
  } catch (error) {
  }
  
  // Create a special payload for school deletion
  // We'll use a special target format to indicate this is a school-based logout
  const payload: LogoutPayload = {
    type: LOGOUT_EVENT_TYPE,
    reason: "school-deleted",
    ts: Date.now(),
    // Store schoolId in targetAdminIds field as a special marker
    // Format: "school:${schoolId}" to distinguish from user IDs
    targetAdminIds: [`school:${schoolIdString}`],
  };
  
  try {
    localStorage.setItem(FORCE_LOGOUT_EVENT_KEY, JSON.stringify(payload));
  } catch (error) {
  }
  
  getChannel()?.postMessage(payload);
  
  // Note: For users in other browsers, they will be logged out via polling
  // when they try to fetch school data and get a 404 response (school deleted)
};

